import APP_CONFIG from 'src/config/AppConfig';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import {
  DEFAULT_TIMEZONE_OFFSET,
  STANDARD_TIMEZONE_ID,
} from 'src/redux/data/loggedInUser/modules/constants';
import { CONTINENTS } from 'src/components/AppLayout/components/constants';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(duration);

export const API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';
export const API_DATE_FORMAT_WITHOUT_MS = 'YYYY-MM-DD';

export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const MEDICAL_REPORT_DATE_FORMAT = 'DD-MM-YYYY';

export const MONT_YEAR_FORMAT = 'MM/YY';

export const GRAPH_DATE_FORMAT = 'MMM DD';
export const GRAPH_DATE_TIME_FORMAT = 'MM/DD HH:mm';
export const EU_GRAPH_DATE_TIME_FORMAT = 'DD/MM HH:mm';

export const CSV_FILE_NAME_DATE_FORMAT = 'MMDDYYYY';
export const EU_CSV_FILE_NAME_DATE_FORMAT = 'DDMMYYYY';

export const FORMAT_WITHOUT_SECONDS = 'MM/DD/YYYY HH:mm';
export const EU_FORMAT_WITHOUT_SECONDS = 'DD/MM/YYYY HH:mm';

export const CSV_FORMAT_WITHOUT_SECONDS = 'MM_DD_YYYY_HH_mm';
export const EU_CSV_FORMAT_WITHOUT_SECONDS = 'DD_MM_YYYY_HH_mm';

export const DATE_AND_12H_TIME_FORMAT = 'MM/DD/YYYY | hh:mm A';
export const EU_DATE_AND_12H_TIME_FORMAT = 'DD/MM/YYYY | HH:mm';

export const MONTH_DAY_YEAR_FORMAT = 'MM/DD/YYYY';
export const EU_MONTH_DAY_YEAR_FORMAT = 'DD/MM/YYYY';
export const MONTH_DAY_FORMAT = 'MM/DD';
export const EU_MONTH_DAY_FORMAT = 'DD/MM';

export const TIME_12H_FORMAT = 'hh:mm A';
export const HOUR_12H_FORMAT = 'hh A';
export const TIME_24H_FORMAT = 'HH:mm';
export const HOUR_24H_FORMAT = 'HH';

export const TIME_24H_FORMAT_WTIH_SECONDS = 'HH:mm:ss';

export const MONTH_SELECTION_FORMAT = 'MMMM YYYY';

export const nowUTC = () => dayjs().utc();
export const nowLocal = () => dayjs();
export const localTimezone = () => dayjs.tz.guess();
export const toDayJs = (dateTime: string) => dayjs(dateTime);
export const toUTC = (time: string | Dayjs) => dayjs(time).utc();
export const isInLast24Hours = (time: string, isUtc = true): boolean => {
  const utcTime = isUtc ? dayjs.utc(time) : toUTC(time);

  return utcTime.isAfter(nowUTC().subtract(1, 'd'));
};

export const getFormatDateByContinent = (dateFormat: string) => {
  if (APP_CONFIG.CONTINENT !== CONTINENTS.EU) {
    return dateFormat;
  }

  switch (dateFormat) {
    case FORMAT_WITHOUT_SECONDS:
      return EU_FORMAT_WITHOUT_SECONDS;
    case DATE_AND_12H_TIME_FORMAT:
      return EU_DATE_AND_12H_TIME_FORMAT;
    case MONTH_DAY_YEAR_FORMAT:
      return EU_MONTH_DAY_YEAR_FORMAT;
    case TIME_12H_FORMAT:
      return TIME_24H_FORMAT;
    case MONTH_DAY_FORMAT:
      return EU_MONTH_DAY_FORMAT;
    case HOUR_12H_FORMAT:
      return HOUR_24H_FORMAT;
    case CSV_FILE_NAME_DATE_FORMAT:
      return EU_CSV_FILE_NAME_DATE_FORMAT;
    case CSV_FORMAT_WITHOUT_SECONDS:
      return EU_CSV_FORMAT_WITHOUT_SECONDS;
    case GRAPH_DATE_TIME_FORMAT:
      return EU_GRAPH_DATE_TIME_FORMAT;
    default:
      return dateFormat;
  }
};

export const toApiCallFormat = (time: string | Dayjs) =>
  toUTC(time).format(API_DATE_FORMAT);
export const toDisplayFormat = (time: string, format = DATE_TIME_FORMAT) =>
  dayjs(time).format(format);

export const getTimezoneId = (timeZoneId?: string | null) =>
  timeZoneId || STANDARD_TIMEZONE_ID;

export const getUtcTimezoneOffsetInMinutes = (
  utcOffsetString: string,
): number => {
  const sign = utcOffsetString.slice(0, 1);
  const offset = utcOffsetString.slice(1);
  const tz = dayjs(offset, TIME_24H_FORMAT);
  const minutesOffset = tz.hour() * 60 + tz.minute();

  return sign === '+' ? minutesOffset : -minutesOffset;
};

export const parseUtcOffsetToHours = (timeZoneId: string | null): string => {
  if (!timeZoneId) {
    return DEFAULT_TIMEZONE_OFFSET as string;
  }

  const timezoneDateString = dayjs().tz(timeZoneId).format();

  return dayjs().tz(timeZoneId).format().slice(-6, timezoneDateString.length);
};

export const convertToTimezone = (
  dateTime: string | Dayjs,
  timezone: string | null | undefined = '',
  isUtc = true,
): dayjs.Dayjs => {
  let dateByTimezone;
  if (timezone) {
    dateByTimezone = isUtc
      ? dayjs.utc(dateTime).tz(timezone)
      : dayjs(dateTime).tz(timezone);
  } else {
    dateByTimezone = dayjs.utc(dateTime);
  }

  const tzUtcOffset = dateByTimezone.utcOffset();

  return isUtc
    ? dayjs.utc(dateTime).utcOffset(tzUtcOffset)
    : dayjs(dateTime).utcOffset(tzUtcOffset);
};

//* Get date and time in a specific timezone
//* Performs no time value conversion only dayjs timezone conversion
export const parseInTimezone = (dateTime: string | Dayjs, timeZone: string) => {
  const utcOffset = dayjs(dateTime).tz(timeZone).utcOffset();
  return utcOffset
    ? dayjs(dateTime).utcOffset(utcOffset, true)
    : dayjs(dateTime).utc(true);
};

//* Supports conversion only for:
//* - UTC time (if flag isUtc is true)
//* - Local Time (if flag isUtc is false)
export const displayInTimezone = (
  dateTime: string | Dayjs,
  timezone: string,
  format: string,
  isUtc = true,
): string =>
  convertToTimezone(dateTime, timezone, isUtc).format(
    getFormatDateByContinent(format),
  );

export const getPastMonths = (numberOfMonths: number, format?: string) => {
  const months = [];

  for (let i = 0; i < numberOfMonths; i++) {
    const date = dayjs().subtract(i, 'month');
    months.push(date.format(format || MONTH_SELECTION_FORMAT));
  }

  return months;
};
